import { ROOT_SHELL_APPS, SURFACE_NAME_MAX_LENGTH, SURFACE_NAME_PATTERN, SURFACE_USE_APP_MAX_LENGTH, SURFACE_USE_APP_PATTERN } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/shell-sync.schemas.ts
/**
* The per-tenant shell setting and its CP to DP sync (layered shell surfaces, R-33, R-34, R-35, R-106,
* R-112): the sync body and the DP's answer to it.
*
* `OrgShellSchema` is the one shape of a tenant's shell setting for every reader and writer:
* - the CP store, `platform.organizations.shell` (jsonb), and its write API
*   `PUT /v1/orgs/:orgId/shell` in platform-api (WS-28);
* - the relay `POST /internal/data-planes/:dpId/shell-sync` in provisioning-svc and
*   `IDpHttpClient.syncShell` (WS-31);
* - the DP receiver `POST /internal/shell-sync` in core-api (WS-5), which upserts the org's `/`
*   row of `auth.root_shell_assignments` from `rootShell` and stores `settings` for
*   `services.settings` (WS-8);
* - the console section on `TenantPane` (WS-29).
*
* The file takes the shape of `branding.schemas.ts` and `branding-sync.schemas.ts`: the types are
* `z.infer<>` of the schemas, so the wire and the TypeScript cannot drift. Every object is
* `.strict()`, so an unknown key at any depth is refused at every hop. In particular a sync body
* names no path: the `/` row is the only row the sync writes (R-34), and a body that carries a
* `path`, `pathPrefix` or `host` key is refused.
*
* No `.transform()` appears here, so every schema stays representable for
* `createOpenApiDocument()` when a controller binds it through `@Body({ schema })`. The refines
* below are dropped from the JSON Schema and still run in the pipe.
*
* This module does no I/O and holds no state.
*/
/** The DP route the CP pushes one org's shell setting to, over mTLS (`CpMtlsGuard`, CN `cp-platform`). */
const SHELL_SYNC_ROUTE = "/internal/shell-sync";
/** The most top-level keys a tenant's `settings` object may hold. */
const SHELL_SETTINGS_MAX_KEYS = 64;
/** The longest top-level settings key, in characters. */
const SHELL_SETTINGS_KEY_MAX_LENGTH = 64;
/**
* The grammar of a top-level settings key: a letter, then letters, digits, `_` and `-`. It admits
* the default shell's `primaryColor` and `accentSecondaryDark` (R-35). A `__proto__` key in the
* JSON text never reaches this grammar: zod 4's record parser drops it, so the parsed object
* carries no such own key and keeps `Object.prototype` (the schema's test pins this).
*/
const SHELL_SETTINGS_KEY_PATTERN = "^[A-Za-z][A-Za-z0-9_-]*$";
/**
* The longest `JSON.stringify(settings)` may be, in UTF-16 code units: 16 KiB of text. The CP
* validates the values against the root shell's own `settings` schema (R-35); this bound stops an
* oversized object at every hop, whatever schema the root declares.
*/
const SHELL_SETTINGS_MAX_JSON_LENGTH = 16384;
/** The message a settings object over the size bound, or one that is not JSON data, earns. */
const SHELL_SETTINGS_SIZE_MESSAGE = `settings must serialise to JSON of at most ${SHELL_SETTINGS_MAX_JSON_LENGTH} characters`;
/** The message an `app` outside `ROOT_SHELL_APPS` earns. */
const ROOT_SHELL_APP_MESSAGE = `app must be one of ROOT_SHELL_APPS (${ROOT_SHELL_APPS.join(", ")})`;
/**
* True when `settings` serialises to JSON within {@link SHELL_SETTINGS_MAX_JSON_LENGTH}. A value
* `JSON.stringify` throws on (a `BigInt`, a cycle) answers false, so a caller that built the
* object in code gets the same refusal as an oversized one. Pure; never throws.
*/
function settingsFitJson(settings) {
	try {
		return JSON.stringify(settings).length <= SHELL_SETTINGS_MAX_JSON_LENGTH;
	} catch {
		return false;
	}
}
/**
* The root shell a tenant's `/` row names: a registered app slug on `ROOT_SHELL_APPS` and one of
* its `root: true` surfaces. `app` takes the app-slug grammar of a `uses[]` row
* (`SURFACE_USE_APP_PATTERN`, at most 30 characters) and must be a `ROOT_SHELL_APPS` entry, so an
* app outside that list is refused at the CP write, the relay and the DP receiver alike (D2).
* `surface` takes the surface-name grammar (`SURFACE_NAME_PATTERN`, at most 64 characters).
* Whether the surface is that app's root is the CP write's check against the pinned manifest and
* the DP's against its stored manifest; the schema cannot see a manifest.
*/
const RootShellTargetSchema = z.object({
	app: z.string().max(SURFACE_USE_APP_MAX_LENGTH).regex(new RegExp(SURFACE_USE_APP_PATTERN)).refine((app) => ROOT_SHELL_APPS.includes(app), { message: ROOT_SHELL_APP_MESSAGE }),
	surface: z.string().max(SURFACE_NAME_MAX_LENGTH).regex(new RegExp(SURFACE_NAME_PATTERN))
}).strict();
/**
* A tenant's settings for its root shell (R-35): an object of at most
* {@link SHELL_SETTINGS_MAX_KEYS} keys in {@link SHELL_SETTINGS_KEY_PATTERN}, whose values are
* JSON data the root shell's declared `settings` schema types. The values are opaque here: the CP
* validates them against the pinned manifest's schema before it writes (WS-29, WS-28), and the
* surface reads them through `services.settings` tolerantly (D8).
*/
const ShellSettingsSchema = z.record(z.string().min(1).max(64).regex(new RegExp(SHELL_SETTINGS_KEY_PATTERN)), z.unknown()).refine((settings) => Object.keys(settings).length <= 64, { message: `settings may hold at most 64 keys` }).refine(settingsFitJson, { message: SHELL_SETTINGS_SIZE_MESSAGE });
/**
* The STORED per-org shell setting, `platform.organizations.shell`, and the CP write body.
*
* - `rootShell` is required (R-78, which amends R-73). Every org row carries one: migration 0011
*   writes it for every existing org, and org creation writes the default
*   `{ app: 'default-shell', surface: 'root-shell.v1' }`. The DP sync makes the org's `/` row
*   name `{app, surface}`.
* - `rootShell` absent: refused. So `{}` and a body with `settings` and no `rootShell` are refused
*   with 400 at the CP write (`PUT /v1/orgs/:orgId/shell`), the relay and the DP receiver alike.
*   The issue path is `['rootShell']`.
* - `settings` absent reads as `{}`: the root shell runs on its declared defaults.
*/
const OrgShellSchema = z.object({
	rootShell: RootShellTargetSchema,
	settings: ShellSettingsSchema.optional()
}).strict();
/**
* The shell setting an org holds when no operator chose one (R-78, R-85). This module is its one
* home; every other copy re-exports it.
*
* - `rootShell` is `default-shell`, the app the loader falls back to (D22), on its root surface
*   `root-shell.v1`.
* - `settings` is `{}`, so the default shell runs on the defaults its own `settings` schema
*   declares (R-35). A new org has no branding yet, so the copy of its four branding colours that
*   R-78 describes is empty.
*
* Readers and writers:
* - platform-api: `OrgService.create` writes it for a new org, and the `organizations.shell`
*   column default and migration 0011 hold the same JSON text
*   (`{"rootShell":{"app":"default-shell","surface":"root-shell.v1"},"settings":{}}`).
*   `default-org-shell.ts` re-exports this constant (R-85).
* - cp-admin-ui: the reset on `TenantShellSection` loads it and Save sends it through
*   `PUT /v1/orgs/:orgId/shell` (WS-29, R-82 E17).
*
* `OrgShellSchema` accepts the value unchanged. The object is frozen at every depth, so a write to
* it throws a `TypeError` in strict-mode code and the value never changes. It holds no state and
* causes no side effect.
*/
const DEFAULT_ORG_SHELL = Object.freeze({
	rootShell: Object.freeze({
		app: "default-shell",
		surface: "root-shell.v1"
	}),
	settings: Object.freeze({})
});
/**
* Body of DP `POST /internal/shell-sync`: the CP pushes one org's whole shell setting.
*
* `orgId` and `orgSlug` take `BrandingSyncRequestSchema`'s rules (a uuid; 2 to 63 characters).
* The DP writer keys its write by `orgSlug`, as `writeBranding` does, so an org the DP has not
* bootstrapped yet updates nothing. The body replaces the org's shell setting whole: the
* required `rootShell` names the org's `/` row (R-78), and `settings` the body omits reads as `{}`.
* A body whose `shell` omits `rootShell` is refused, so no sync removes the `/` row.
*
* `revision` (R-106, R-112) is optional on the wire. A CP from stage 5a on always sends it
* (platform-api `ShellService`, WS-54); a CP before 5a, or one rolled back to it, sends none.
* - The value is the CP row's `updated_at` as `Date.prototype.toISOString()` writes it,
*   `YYYY-MM-DDTHH:mm:ss.sssZ`, 24 characters (`z.iso.datetime({ precision: 3 })`). A revision
*   with an offset other than `Z`, a precision other than milliseconds, or a type other than a
*   string is refused with 400 on the path `['revision']`.
* - The fixed width makes string order equal time order, so the DP compares two revisions as
*   strings.
*
* The DP writer (core-api `ShellSyncService`, WS-50) stores the revision of the last applied body
* in `auth.root_shell_assignments.sync_revision` (R-119). A null stored revision is earlier than
* every revision. The DP answers with a {@link ShellSyncAnswer}:
* - a revision later than the stored one: the DP applies the body, stores the revision and
*   answers `{ synced: true }`;
* - a revision equal to the stored one: a repeat. The DP writes nothing and answers
*   `{ synced: true }`, so a re-push of an applied body (the WS-54 boot sweep) converges;
* - a revision earlier than the stored one: stale. The DP writes nothing and answers
*   `{ synced: false, reason: 'stale' }`;
* - no revision: the DP applies the body as it did before 5a, whatever it stored, and sets the
*   stored revision to null. So the next body with a revision applies.
* So of two concurrent CP writes that both carry a revision, the later one is the DP's end state,
* whatever order the pushes arrive in.
*
* The schema puts no bound on the instant (R-119, WS-41 E-8 option a): every year from 0000 to
* 9999 passes the parse. The DP writer holds the bounds and refuses each with 400:
* - a revision in a year from 0000 to 0099;
* - a revision later than the DP clock plus 300 seconds.
*
* Version skew (R-112): a DP before 5a parses this body with the schema of that release, where
* `revision` is an unknown key, and answers 400. The CP records `synced: false` for that push.
*/
const ShellSyncRequestSchema = z.object({
	orgId: z.string().uuid(),
	orgSlug: z.string().min(2).max(63),
	revision: z.iso.datetime({ precision: 3 }).optional(),
	shell: OrgShellSchema
}).strict();
/**
* The DP's 2xx answer to `POST /internal/shell-sync` (R-106, R-112), the one declaration of it.
*
* Readers and writers, each of which imports this schema and keeps no copy:
* - core-api `ShellSyncService` (WS-50) writes it;
* - provisioning-svc's relay `POST /internal/data-planes/:dpId/shell-sync` (WS-53) parses the DP
*   body with it and passes the body through; a DP 2xx the parse refuses answers 424;
* - platform-api's `HttpShellSyncClient` (WS-54) parses the relay body with it and records
*   `synced` in its audit row.
*
* Accepted shapes, and nothing else:
* - `{ synced: true }`: the DP applied the body, or the body repeated the stored revision;
* - `{ synced: false }`: the DP holds no org with the body's `orgSlug`;
* - `{ synced: false, reason: 'stale' }`: the body's revision is earlier than the stored one.
*
* Refusals: a value that is not an object, an absent or non-boolean `synced`, a `reason` other than
* `'stale'`, and any key outside `synced` and `reason` (the object is strict). A `reason` beside
* `synced: true` is refused on the path `['reason']` with the message
* `reason 'stale' is valid only beside synced: false`. The refine is dropped from the JSON Schema and still
* runs in a parse. The schema has no `.transform()`. Pure; it holds no state.
*/
const ShellSyncAnswerSchema = z.strictObject({
	synced: z.boolean(),
	reason: z.enum(["stale"]).optional()
}).refine((answer) => answer.reason === void 0 || !answer.synced, {
	message: "reason 'stale' is valid only beside synced: false",
	path: ["reason"]
});
//#endregion
export { DEFAULT_ORG_SHELL, OrgShellSchema, ROOT_SHELL_APP_MESSAGE, RootShellTargetSchema, SHELL_SETTINGS_KEY_MAX_LENGTH, SHELL_SETTINGS_KEY_PATTERN, SHELL_SETTINGS_MAX_JSON_LENGTH, SHELL_SETTINGS_MAX_KEYS, SHELL_SETTINGS_SIZE_MESSAGE, SHELL_SYNC_ROUTE, ShellSettingsSchema, ShellSyncAnswerSchema, ShellSyncRequestSchema };

//# sourceMappingURL=shell-sync.schemas.js.map